import * as THREE from 'three';

export const LEGACY_BUILDING_PLACEMENT_SOUND = '/legacy/sound/misc/placebuilding.ogg';
export const LEGACY_BUILDING_BOUNCE_AMPLITUDE = 0.3;
export const LEGACY_BUILDING_BOUNCE_ANGULAR_SPEED = 20;
export const LEGACY_BUILDING_BOUNCE_DURATION_SECONDS = 1.5;

/**
 * Reproduces `ScaleBounceController`: only the model's Y scale oscillates, and the amplitude
 * falls linearly to zero over the lifetime of the controller.
 */
export function legacyBuildingBounceYScale(elapsedSeconds: number): number {
  if (
    !Number.isFinite(elapsedSeconds) ||
    elapsedSeconds <= 0 ||
    elapsedSeconds >= LEGACY_BUILDING_BOUNCE_DURATION_SECONDS
  ) {
    return 1;
  }
  const envelope =
    (LEGACY_BUILDING_BOUNCE_DURATION_SECONDS - elapsedSeconds) /
    LEGACY_BUILDING_BOUNCE_DURATION_SECONDS;
  return (
    1 +
    Math.sin(LEGACY_BUILDING_BOUNCE_ANGULAR_SPEED * elapsedSeconds) *
      LEGACY_BUILDING_BOUNCE_AMPLITUDE *
      envelope
  );
}

// Resource instances survive harvests and regrowth. Their resting matrices must follow
// readiness, rather than being sampled once from a batch containing hidden instances.
const restingMatrices = new WeakMap<THREE.InstancedMesh, Map<number, THREE.Matrix4>>();
const bounceMatrix = new THREE.Matrix4();
const bounceScale = new THREE.Vector3();

/** The supplied resting matrix is retained and must not be mutated by the caller. */
export function setLegacyBuildingBounceBaseMatrix(
  mesh: THREE.InstancedMesh,
  index: number,
  matrix: THREE.Matrix4,
): void {
  let matrices = restingMatrices.get(mesh);
  if (matrices === undefined) {
    matrices = new Map();
    restingMatrices.set(mesh, matrices);
  }
  matrices.set(index, matrix);
}

export function applyLegacyBuildingBounceScale(
  mesh: THREE.InstancedMesh,
  index: number,
  yScale: number,
): void {
  if (index < 0 || index >= mesh.count) return;
  let base = restingMatrices.get(mesh)?.get(index);
  if (base === undefined) {
    base = new THREE.Matrix4();
    mesh.getMatrixAt(index, base);
    setLegacyBuildingBounceBaseMatrix(mesh, index, base);
  }
  // Scaling the basis directly preserves hidden (singular) matrices, rotation, and scale.
  // Matrix4.decompose turns a zero-scale hidden matrix into unit scale.
  bounceMatrix.copy(base).scale(bounceScale.set(1, yScale, 1));
  mesh.setMatrixAt(index, bounceMatrix);
  mesh.instanceMatrix.addUpdateRange(index * 16, 16);
  mesh.instanceMatrix.needsUpdate = true;
}
